import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdChevronLeft } from 'react-icons/md';
import { Button, Divider, IconButton, ModalBody, ModalFooter, ModalHeader, TmdxTextarea } from '@ds';
import { useCaseNotesStore } from '@/store/case-notes.store';
import { CaseNote } from '@/api/apis/case';
import { useUpdateCaseNoteMutation } from '@/queries/case';
import { NOTE_MAX_LENGTH } from './constants';

export const EditNoteModalContent = ({ caseId }: { caseId: string }) => {
  const { isPending, mutateAsync: submitCaseNoteUpdate } = useUpdateCaseNoteMutation();
  const setEditNote = useCaseNotesStore.use.setEditNote();
  const note = useCaseNotesStore.use.editNote() as CaseNote;
  const { control, watch, handleSubmit, getValues, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      message: note?.message,
    },
  });

  const saveBtnDisabled = useMemo(() => {
    return Boolean(!formState.isValid || watch('message') === note?.message);
  }, [formState, watch, note?.message]);

  const closeEditNote = () => setEditNote(null);

  const onEditSubmit = async () => {
    await submitCaseNoteUpdate({ caseId, noteId: note.noteId, message: getValues('message') });
    closeEditNote();
  };

  return (
    <>
      <ModalHeader>
        <IconButton size="xs" variant="outline" startContent={<MdChevronLeft />} onPress={closeEditNote} />
        <h3 className="ml-3">Edit Note</h3>
      </ModalHeader>
      <div className="px-6">
        <Divider />
      </div>
      <ModalBody className="flex-none py-6">
        <form id="edit-note-form" onSubmit={handleSubmit(onEditSubmit)}>
          <Controller
            name="message"
            control={control}
            rules={{ required: true, maxLength: NOTE_MAX_LENGTH }}
            render={({ field, fieldState: { invalid, error } }) => (
              <TmdxTextarea
                fullWidth
                disableAnimation
                minRows={6}
                maxLength={NOTE_MAX_LENGTH}
                placeholder="Type here"
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
              />
            )}
          />
        </form>
      </ModalBody>
      <div className="px-6">
        <Divider />
      </div>
      <ModalFooter>
        <div className="flex gap-3">
          <Button variant="outline" onPress={closeEditNote} className="flex-1">
            Discard
          </Button>
          <Button
            type="submit"
            form="edit-note-form"
            variant="solid"
            isDisabled={saveBtnDisabled}
            isLoading={isPending}
            className="flex-1 text-base-90"
          >
            Save Changes
          </Button>
        </div>
      </ModalFooter>
    </>
  );
};
